import { ExternalLink, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { EXAMS, PROGRAM_DOCS } from "../data/exams";
import { FAQS } from "../data/faq";
import { GuideTabs } from "../components/GuideTabs";
import { Inline } from "../components/RichText";
import { ExamBadge, useDocumentTitle } from "../components/ui";
import { pick, useLang, useT, type L } from "../i18n";

const POLICIES: { title: L; body: L }[] = [
  {
    title: { vi: "Điều kiện & đăng ký", en: "Eligibility & registration" },
    body: {
      vi: "Dành cho nhân viên tổ chức thuộc **Claude Partner Network**, từ 18 tuổi. Đăng ký qua Anthropic Partner Academy, thi qua **Pearson VUE** (online có giám sát hoặc tại trung tâm). Partner hạng Select, Preferred, Global Premier được giảm 50% lệ phí.",
      en: "Open to employees of **Claude Partner Network** organizations, aged 18+. Register through the Anthropic Partner Academy; exams are delivered by **Pearson VUE** (online proctored or test center). Select, Preferred and Global Premier partners get 50% off.",
    },
  },
  {
    title: { vi: "Ngày thi & giấy tờ", en: "Exam day & ID" },
    body: {
      vi: "Mang giấy tờ tùy thân có ảnh, còn hạn, tên khớp với tên đăng ký. Thi online cần phòng riêng, bàn trống, webcam và kiểm tra hệ thống trước. Thi **đóng sách** — không tài liệu, không dùng AI.",
      en: "Bring valid government photo ID matching your registration name. Online delivery needs a private room, clear desk, webcam and a system check beforehand. The exam is **closed book** — no materials, no AI tools.",
    },
  },
  {
    title: { vi: "Hủy & đổi lịch", en: "Cancel & reschedule" },
    body: {
      vi: "Hủy hoặc đổi lịch **ít nhất 24 giờ** trước giờ thi; muộn hơn hoặc vắng mặt sẽ mất lệ phí.",
      en: "Cancel or reschedule **at least 24 hours** before your appointment; later changes or no-shows forfeit the fee.",
    },
  },
  {
    title: { vi: "Thi lại", en: "Retakes" },
    body: {
      vi: "Chờ **14 ngày** sau lần trượt thứ nhất, **30 ngày** sau lần thứ hai, **90 ngày** sau lần thứ ba; tối đa **4 lần**/đề trong 12 tháng cuốn chiếu. Mỗi lần thi tính phí.",
      en: "Wait **14 days** after the first failure, **30 days** after the second, **90 days** after the third; at most **4 attempts** per exam in a rolling 12 months. Each attempt is charged.",
    },
  },
  {
    title: { vi: "Hỗ trợ đặc biệt", en: "Accommodations" },
    body: {
      vi: "Thí sinh cần điều chỉnh (thêm giờ, hỗ trợ tiếp cận…) gửi yêu cầu qua Pearson VUE **trước khi** đặt lịch.",
      en: "Candidates needing accommodations (extra time, accessibility support…) request them through Pearson VUE **before** scheduling.",
    },
  },
  {
    title: { vi: "Bảo mật đề & quy tắc", en: "NDA & conduct" },
    body: {
      vi: "Phải chấp nhận **thỏa thuận bảo mật (NDA)** trước khi làm bài. Chia sẻ, ghi nhớ để phát tán hay dùng đề \"dump\" có thể dẫn tới hủy kết quả, thu hồi chứng chỉ và cấm thi.",
      en: "You must accept a **non-disclosure agreement** before the exam. Sharing content or using \"dumps\" can lead to score cancellation, credential revocation and a testing ban.",
    },
  },
  {
    title: { vi: "Kết quả & hiệu lực", en: "Results & validity" },
    body: {
      vi: "Thang **100–1000**, đạt từ **720**; phiếu điểm cho tỉ lệ đúng theo domain. Chứng chỉ hiệu lực **12 tháng**, gia hạn đúng hạn bằng bài đánh giá **miễn phí, không giám sát**; để hết hạn phải thi lại với lệ phí đầy đủ.",
      en: "Scaled **100–1000**, pass at **720**; the score report shows percent correct by domain. Credentials are valid **12 months**; renew on time with a **free, non-proctored** assessment, or retake at full price after lapsing.",
    },
  },
  {
    title: { vi: "Khiếu nại", en: "Appeals" },
    body: {
      vi: "Khiếu nại (sự cố buổi thi hoặc thắc mắc kết quả) gửi tới **bộ phận hỗ trợ Pearson VUE** trong vòng **14 ngày** kể từ ngày thi/ngày nhận thông báo. Không khiếu nại được về cách đặt điểm chuẩn hay nội dung câu hỏi.",
      en: "Appeals (exam-day incidents or score concerns) go to **Pearson VUE support** within **14 days** of the exam or notification. Standard setting and item content cannot be appealed.",
    },
  },
];

export function GuidePage() {
  const t = useT();
  const lang = useLang();
  useDocumentTitle(t("Cẩm nang thi", "Exam guide"));
  return (
    <div className="cert-guide-page">
      <h1>{t("Cẩm nang thi chứng chỉ Claude", "Claude certification guide")}</h1>
      <p className="muted">
        {t(
          `Tổng hợp từ exam guide ${PROGRAM_DOCS.guideVersion} và chính sách chính thức của Anthropic. Chính sách có thể thay đổi — luôn đối chiếu nguồn gốc trước khi đăng ký.`,
          `Compiled from the ${PROGRAM_DOCS.guideVersion} exam guides and Anthropic's official policies. Policies change — always check the source before registering.`,
        )}
      </p>
      <GuideTabs />

      <h2>{t("Thể thức bốn kỳ thi", "The four exams at a glance")}</h2>
      <div className="card table-wrap">
        <table className="history guide-table">
          <thead>
            <tr>
              <th>{t("Chứng chỉ", "Certification")}</th>
              <th>{t("Số câu", "Items")}</th>
              <th>{t("Thời gian", "Time")}</th>
              <th>{t("Điểm đạt", "Passing")}</th>
              <th>{t("Lệ phí", "Fee")}</th>
              <th>Guide</th>
            </tr>
          </thead>
          <tbody>
            {EXAMS.map((e) => (
              <tr key={e.id}>
                <td><Link to={`/exams/${e.id}`} className="row" style={{ gap: "0.5rem" }}><ExamBadge examId={e.id} size={24} /><span>{e.code}</span></Link></td>
                <td>{e.items}{e.structure && <div className="tiny muted">{pick(e.structure, lang)}</div>}</td>
                <td>{e.durationMin} {t("phút", "min")}</td>
                <td>720 / 1000</td>
                <td>${e.priceUsd}</td>
                <td><a href={e.guideUrl} target="_blank" rel="noreferrer">PDF <ExternalLink size={14} aria-hidden="true" /></a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted">
        {t(
          "Tất cả: trắc nghiệm một hoặc nhiều đáp án (đề ghi rõ số đáp án cần chọn), ngôn ngữ tiếng Anh và tiếng Nhật, không có điều kiện tiên quyết.",
          "All exams: single- and multiple-response items (each states how many to select), offered in English and Japanese, no prerequisites.",
        )}
      </p>

      <div className="section-title"><h2>{t("Chính sách", "Policies")}</h2></div>
      <div className="grid grid-2">
        {POLICIES.map((x) => (
          <div key={x.title.en} className="card">
            <h3>{pick(x.title, lang)}</h3>
            <p className="small mb-0"><Inline text={pick(x.body, lang)} /></p>
          </div>
        ))}
      </div>
      <p className="small mt">
        <a href={PROGRAM_DOCS.policyUrl} target="_blank" rel="noreferrer">{t("Chính sách kỳ thi (PDF)", "Exam policy (PDF)")} <ExternalLink size={14} aria-hidden="true" /></a>
        {" · "}
        <a href={PROGRAM_DOCS.termsUrl} target="_blank" rel="noreferrer">{t("Điều khoản chứng chỉ (PDF)", "Certification terms (PDF)")} <ExternalLink size={14} aria-hidden="true" /></a>
        {" · "}
        <a href={PROGRAM_DOCS.accommodationsUrl} target="_blank" rel="noreferrer">{t("Hỗ trợ đặc biệt (Pearson VUE)", "Accommodations (Pearson VUE)")} <ExternalLink size={14} aria-hidden="true" /></a>
        {" · "}
        <a href="https://www.pearsonvue.com/us/en/anthropic.html" target="_blank" rel="noreferrer">{t("Hỗ trợ Pearson VUE", "Pearson VUE support")} <ExternalLink size={14} aria-hidden="true" /></a>
      </p>
      <p className="small muted">
        <Mail className="ico" size={14} aria-hidden="true" />{" "}
        {t("Email ", "")}<a href={`mailto:${PROGRAM_DOCS.supportEmail}`}>{PROGRAM_DOCS.supportEmail}</a>{" "}
        {t("chỉ dùng để sửa tên đăng ký; mọi câu hỏi khác (lịch thi, kỹ thuật, khiếu nại) gửi Pearson VUE.", "is only for correcting your registration name; everything else (scheduling, technical issues, appeals) goes to Pearson VUE.")}
      </p>

      <div className="section-title"><h2>{t("Câu hỏi thường gặp", "FAQ")}</h2></div>
      <div className="card faq">
        {FAQS.map((f) => (
          <details key={f.q.en}>
            <summary>{pick(f.q, lang)}</summary>
            <p className="mb-0 mt"><Inline text={pick(f.a, lang)} /></p>
          </details>
        ))}
      </div>
    </div>
  );
}
